import { FC, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Divider,
  Input,
  InputSelect,
  Label,
  Textarea,
} from '@features/ui';
import {
  LeelooAccounts,
  EditBalanceFormData,
  editBalanceSchema,
} from '@src/types/users.ts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { extraTradingDays } from '@features/admin/users/usersData.ts';
import { formatCurrency } from '@lib/utils.ts';

interface EditAccountBalancePopupProps {
  open: boolean;
  account: LeelooAccounts | null;
  onOpenChange: (open: boolean) => void;
  onSave: (data: EditBalanceFormData) => void;
}
export const EditAccountBalancePopup: FC<EditAccountBalancePopupProps> = ({
  open,
  account,
  onOpenChange,
  onSave,
}) => {
  const {
    control,
    // watch,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditBalanceFormData>({
    resolver: yupResolver(editBalanceSchema),
  });

  // const currentBalance = watch('current_balance');

  useEffect(() => {
    if (open) {
      reset({
        current_balance: Number(account?.account_balance),
        new_balance: undefined,
        extra_trading_days: undefined,
        notes: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.account_balance]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={'xl'}>
        <DialogHeader>
          <DialogTitle className="text-2xl">{`Edit Account > ${account?.rithmic_account_name ?? ''}`}</DialogTitle>
          <Divider />
        </DialogHeader>
        <form autoComplete="off" onSubmit={handleSubmit(onSave)}>
          <div className={'text-xs'}>
            {/* {currentBalance ? ( */}
            <>
              <p className={'font-bold text-sm'}>Add Trading Days</p>
              <p className={'my-2'}>
                You can add trading days to this account if you have allowed
                carry over from a closed Eval account.
              </p>
              <p className={'my-4'}>
                <strong>Current Trading days</strong>:{' '}
                {account?.trading_days ?? 0}
              </p>
              <Controller
                control={control}
                name="extra_trading_days"
                render={({ field }) => (
                  <div className={'flex-1'}>
                    <Label
                      className="text-xs mb-2"
                      htmlFor="extra_trading_days"
                    >
                      Choose how many days to add
                    </Label>
                    <InputSelect
                      className={'text-xs'}
                      options={extraTradingDays}
                      placeholder={'Select'}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    {errors.extra_trading_days && (
                      <p className="mt-1 text-red-500">
                        {errors.extra_trading_days.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Divider className={'my-4'} />
            </>
            {/* ) : null} */}
            <p className={'font-bold text-sm'}>Edit Account Balance</p>
            <p className={'my-2'}>
              By changing the balance and adding days you can force this account
              to qualify.
            </p>
            <div className={'flex mt-4 gap-5'}>
              <div className={'flex-1'}>
                <Label className="text-xs mb-2" htmlFor="current_balance">
                  Current Balance $
                </Label>
                <Input
                  id="current_balance"
                  placeholder={'Enter'}
                  readOnly={true}
                  textSize={'xs'}
                  value={`${formatCurrency(Number(account?.account_balance), 2)}`}
                  variant={'secondary'}
                />
              </div>
              <Controller
                control={control}
                name="new_balance"
                render={({ field }) => (
                  <div className={'flex-1'}>
                    <Label className="text-xs mb-2" htmlFor="new_balance">
                      New Balance $
                    </Label>
                    <Input
                      {...field}
                      id="new_balance"
                      placeholder={'Enter'}
                      textSize={'xs'}
                      type={'number'}
                      variant={'secondary'}
                    />
                    {errors.new_balance && (
                      <p className="mt-1 text-red-500">
                        {errors.new_balance.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
            <Controller
              control={control}
              name="notes"
              render={({ field }) => (
                <div>
                  <Label className="text-xs mb-2 mt-4" htmlFor="notes">
                    Reason for making the change?
                  </Label>
                  <Textarea
                    {...field}
                    hiddenHeader
                    className={'h-20'}
                    id="notes"
                    placeholder={'Enter'}
                  />
                  {errors.notes && (
                    <p className="mt-1 text-red-500">{errors.notes.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div className="flex items-center gap-4 mt-10">
            <Button
              loading={isSubmitting}
              size={'sm'}
              type={'submit'}
              variant={'lime'}
            >
              Save
            </Button>
            <Button
              className={'w-20'}
              size={'sm'}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
